/**
 * ContextMenuItem — описание одного пункта доменного контекстного меню.
 *
 * Домен отдаёт массив таких пунктов через геттер сервиса (например
 * AssembliesService.contextMenu), а грид рендерит их и в кебаб-колонке,
 * и в меню по правому клику.
 *
 * Все колбэки получают строку (row), потому что доступность/видимость
 * пункта обычно зависит от конкретной записи.
 */
class ContextMenuItem<TRow = any> {
  /** Подпись пункта */
  public title: ((row: TRow) => string) | string;

  /** Material-иконка слева */
  public icon: string;

  /**
   * Действие по клику. Получает строку, может вернуть промис.
   * Ровно true — состояние изменилось: грид обновляется, карточка (VDialogV3)
   * вызывает onContextMenuAction или закрывается. false / ничего — ничего не происходит.
   */
  public callback: (row: TRow) => void | boolean | Promise<void | boolean>;

  /**
   * Причина недоступности пункта.
   * - строка        — пункт всегда задисейблен, строка идёт в title (тултип)
   * - функция       — вычисляется от строки: вернула строку → задисейблен с этим текстом, null → доступен
   * - null / не задан — доступен
   */
  public disabled: ((row: TRow) => string | null) | string | null;

  /**
   * Скрыть пункт целиком (аналог v-if).
   * - boolean  — статично
   * - функция  — вычисляется от строки
   * - не задан — пункт виден
   */
  public hidden: ((row: TRow) => boolean) | boolean;

  /** Нарисовать разделитель <hr> после этого пункта */
  public dividerAfter: boolean;

  /** Пункт встаёт под курсор, пункты над ним уходят вверх. Ставится через makeAnchor() */
  public isAnchor: boolean = false;

  constructor(
    title:                ((row: TRow) => string) | string,
    icon:                 string,
    callback:             (row: TRow) => void | boolean | Promise<void | boolean>,
    disabled:             ((row: TRow) => string | null) | string | null = null,
    hidden:               ((row: TRow) => boolean) | boolean              = false,
    dividerAfter:         boolean                                         = false,
  ) {
    this.title                = title;
    this.icon                 = icon;
    this.callback             = callback;
    this.disabled             = disabled;
    this.hidden               = hidden;
    this.dividerAfter         = dividerAfter;
  }

  /**
   * Пункт «Фильтровать по «<подпись>»»: отдаёт в onFilterChange колонку и значение её ячейки.
   * Подпись — toString() сущности. Пустая ячейка — пункт недоступен.
   *
   * @example
   * ContextMenuItem.filterBy('seller', this.onFilterChange)
   */
  static filterBy<TRow extends Record<string, any>>(
    columnId: string,
    onFilterChange: (columnId: string, value: unknown) => void,
  ): ContextMenuItem<TRow> {
    const isEmpty = (row: TRow) => row[columnId] === null || row[columnId] === undefined;

    return new this<TRow>(
      (row) => isEmpty(row) ? 'Фильтровать по значению' : `Фильтровать по «${row[columnId].toString()}»`,
      'filter_alt',
      (row) => { onFilterChange(columnId, row[columnId]); },
      (row) => isEmpty(row) ? 'Значение не заполнено' : null,
      false,
      true,
    );
  }

  /** Делает пункт точкой отсчёта меню: он встанет под курсор, пункты над ним уйдут вверх */
  makeAnchor(): this {
    this.isAnchor = true;
    return this;
  }
}

export default ContextMenuItem;